import { useEffect, useState } from 'react'
import AppHeader from '../../components/shell/AppHeader'
import SupervisorNav from '../../components/supervisor/SupervisorNav'
import { useProfileIdentity } from '../../lib/useProfileIdentity'
import EmptyState from '../../components/shell/EmptyState'
import { supabase } from '../../lib/supabase'
import { useSupervisorTeams } from './useSupervisorTeams'
import TeamSwitcher from '../../components/supervisor/TeamSwitcher'
import {
  addStaffingTarget,
  getStaffingTargets,
  weekdaysBetween,
  getTargetCatalog,
  removeStaffingTarget,
  type StaffingTarget,
  type TargetCatalog,
  type TargetScope,
} from './staffingTargetStore'

/**
 * docs/product/coverage-and-insights.md's "Staffing targets": a supervisor
 * configures minimum staffing by date or day of week, shift, assignment,
 * team, and qualification, each with an effective date range, stored in
 * muster.staffing_targets. The shift is shown as a plain fact, the team's
 * own active shift template; assignment and qualification are real
 * choices over the assignment and qualification catalogs. The daily
 * assignment board's own coverage strip (SupervisorDailyAssignmentsPage.tsx)
 * and the dashboard's coverage gaps read the winning target for the viewed
 * date through staffingTargetStore.ts's resolveTarget, and narrow the
 * actual count to a target's own qualified people where one is named. Any
 * assignment (target.assignment left unset) is still a choice here;
 * resolveTarget's own matching leaves it out of every per-assignment
 * lookup rather than applying one minimum to each assignment's own tile
 * at once.
 *
 * The team toggle (TeamSwitcher.tsx) lists the teams the signed-in
 * supervisor owns, per docs/product/personas.md's "A supervisor owns one
 * or more teams," and scopes both the list and Create a target's own new
 * entries to one of them. Creating and removing a target are each an
 * audit event (20260925000000_staffing_target_writes.sql).
 */

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

function targetScopeLabel(target: StaffingTarget): string {
  return target.scope === 'date' ? (target.date ?? '') : target.weekday ?? ''
}

export default function SupervisorStaffingTargetsPage() {
  const identity = useProfileIdentity()
  const [userId, setUserId] = useState<string | null>(null)
  const [supervisorName, setSupervisorName] = useState<string | null>(null)
  const [sessionReady, setSessionReady] = useState(false)
  const { teams, currentTeamId, teamsLoaded, switchTeam: switchToTeam } = useSupervisorTeams(userId)
  const [targets, setTargets] = useState<StaffingTarget[]>([])
  const [catalog, setCatalog] = useState<TargetCatalog>({ shift: null, assignments: [], qualifications: [] })
  const [formOpen, setFormOpen] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)

  const [scope, setScope] = useState<TargetScope>('weekday')
  const [weekday, setWeekday] = useState(WEEKDAYS[0])
  const [weekdayThrough, setWeekdayThrough] = useState('')
  const [date, setDate] = useState('')
  const [assignment, setAssignment] = useState('')
  const [qualification, setQualification] = useState('')
  const [minimumHeadcount, setMinimumHeadcount] = useState('')
  const [effectiveStart, setEffectiveStart] = useState('')
  const [effectiveEnd, setEffectiveEnd] = useState('')

  // Waits on the profile fetch too, rather than resolving sessionReady as
  // soon as the session itself does, the same race
  // SupervisorApprovalInboxPage.tsx's own sessionReady closes for
  // supervisorName: by the time this page is interactive, Save target
  // already has a real identity to record it under.
  useEffect(() => {
    let cancelled = false
    supabase.auth
      .getSession()
      .then(async ({ data: { session } }) => {
        if (cancelled) return
        const id = session?.user.id
        if (id) {
          setUserId(id)
          setTargets(await getStaffingTargets())
          const { data: profile } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', id)
            .single()
          if (!cancelled && profile) {
            setSupervisorName(`${profile.first_name} ${profile.last_name}`)
          }
        }
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // The assignments and qualifications this team can choose from, and its
  // own shift, reloaded whenever the team changes.
  useEffect(() => {
    if (!currentTeamId) return
    let cancelled = false
    getTargetCatalog(currentTeamId).then((loaded) => {
      if (!cancelled) setCatalog(loaded)
    })
    return () => {
      cancelled = true
    }
  }, [currentTeamId])

  const parsedMinimum = Number(minimumHeadcount)
  const canSave =
    !!supervisorName &&
    !!catalog.shift &&
    effectiveStart !== '' &&
    (effectiveEnd === '' || effectiveEnd >= effectiveStart) &&
    minimumHeadcount !== '' &&
    Number.isFinite(parsedMinimum) &&
    parsedMinimum >= 0 &&
    (scope === 'weekday' || date !== '') &&
    (scope !== 'weekday' || weekdaysBetween(weekday, weekdayThrough || weekday).length > 0)

  function resetForm() {
    setScope('weekday')
    setWeekday(WEEKDAYS[0])
    setWeekdayThrough('')
    setDate('')
    setAssignment('')
    setQualification('')
    setMinimumHeadcount('')
    setEffectiveStart('')
    setEffectiveEnd('')
  }

  async function saveTarget() {
    if (!userId || !supervisorName || !catalog.shift || !canSave) {
      setSaveFailed(true)
      return
    }
    // A range of weekdays is one target for each day in it, so each can be
    // removed or overridden on its own.
    const days = scope === 'weekday' ? weekdaysBetween(weekday, weekdayThrough || weekday) : [undefined]
    const added: StaffingTarget[] = []
    for (const day of days) {
      const saved = await addStaffingTarget(userId, {
        teamId: currentTeamId,
        shiftTemplateId: catalog.shift.id,
        scope,
        date: scope === 'date' ? date : undefined,
        weekday: day,
        assignmentId: assignment || undefined,
        qualificationId: qualification || undefined,
        minimumHeadcount: parsedMinimum,
        effectiveStart,
        effectiveEnd: effectiveEnd || undefined,
      })
      if (!saved) {
        setTargets((current) => [...current, ...added])
        setSaveFailed(true)
        return
      }
      added.push(saved)
    }
    setSaveFailed(false)
    setTargets((current) => [...current, ...added])
    resetForm()
    setFormOpen(false)
  }

  async function removeTarget(id: string) {
    if (!(await removeStaffingTarget(id))) {
      setSaveFailed(true)
      return
    }
    setSaveFailed(false)
    setTargets((current) => current.filter((target) => target.id !== id))
  }

  function switchTeam(nextTeamId: string) {
    if (nextTeamId === currentTeamId) return
    switchToTeam(nextTeamId)
    setFormOpen(false)
  }

  const teamName = teams.find((team) => team.id === currentTeamId)?.name ?? ''
  const teamTargets = targets.filter((target) => target.teamId === currentTeamId)

  if (!sessionReady || (userId && !teamsLoaded)) {
    return (
      <div className="flex h-dvh flex-col bg-surface text-ink">
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-4">
          <p role="status" className="text-small text-ink-muted">
            Loading&hellip;
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-surface text-ink">
      <a
        href="#staffing-targets-main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-2 focus:rounded focus:bg-canvas focus:px-4 focus:py-2 focus:font-semibold"
      >
        Skip to main content
      </a>

      <AppHeader initials={identity?.initials ?? ''} />

      <main id="staffing-targets-main" tabIndex={0} className="flex flex-1 flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">
        <SupervisorNav />
        <div className="mx-auto flex w-full max-w-[1100px] flex-1 flex-col gap-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-h2 font-extrabold">Staffing Targets</h1>
              <p className="mt-1 text-small text-ink-muted">{teamName}</p>
            </div>
            <button
              type="button"
              aria-expanded={formOpen}
              onClick={() => setFormOpen((open) => !open)}
              className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover"
            >
              Create a target
            </button>
          </div>

          <TeamSwitcher teams={teams} currentTeamId={currentTeamId} onChange={switchTeam} />

          {saveFailed && (
            <p role="alert" className="text-caption font-semibold text-error">
              Could not save the target. Try again.
            </p>
          )}

          {formOpen && (
            <div className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3">
              <div role="group" aria-label="Applies to" className="flex gap-2">
                <button
                  type="button"
                  aria-pressed={scope === 'weekday'}
                  onClick={() => setScope('weekday')}
                  className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                    scope === 'weekday' ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
                  }`}
                >
                  A day of the week
                </button>
                <button
                  type="button"
                  aria-pressed={scope === 'date'}
                  onClick={() => setScope('date')}
                  className={`flex min-h-11 items-center justify-center rounded-control border px-3 text-small font-bold ${
                    scope === 'date' ? 'border-primary bg-selected' : 'border-line-strong hover:bg-surface-2'
                  }`}
                >
                  A specific date
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {scope === 'weekday' ? (
                  <>
                    <div>
                      <label htmlFor="target-weekday" className="mb-1 block text-caption font-bold text-ink-muted">
                        Weekday
                      </label>
                      <select
                        id="target-weekday"
                        value={weekday}
                        onChange={(event) => {
                          setWeekday(event.target.value)
                          setWeekdayThrough('')
                        }}
                        className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                      >
                        {WEEKDAYS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="target-weekday-through" className="mb-1 block text-caption font-bold text-ink-muted">
                        Through (optional)
                      </label>
                      <select
                        id="target-weekday-through"
                        value={weekdayThrough}
                        onChange={(event) => setWeekdayThrough(event.target.value)}
                        className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                      >
                        <option value="">Same day</option>
                        {WEEKDAYS.slice(WEEKDAYS.indexOf(weekday) + 1).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <div>
                    <label htmlFor="target-date" className="mb-1 block text-caption font-bold text-ink-muted">
                      Date
                    </label>
                    <input
                      id="target-date"
                      type="date"
                      value={date}
                      onChange={(event) => setDate(event.target.value)}
                      className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                    />
                  </div>
                )}

                <div>
                  <label htmlFor="target-assignment" className="mb-1 block text-caption font-bold text-ink-muted">
                    Assignment
                  </label>
                  <select
                    id="target-assignment"
                    value={assignment}
                    onChange={(event) => setAssignment(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                  >
                    <option value="">Any assignment</option>
                    {catalog.assignments.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="target-qualification" className="mb-1 block text-caption font-bold text-ink-muted">
                    Qualification
                  </label>
                  <select
                    id="target-qualification"
                    value={qualification}
                    onChange={(event) => setQualification(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                  >
                    <option value="">None required</option>
                    {catalog.qualifications.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="target-minimum" className="mb-1 block text-caption font-bold text-ink-muted">
                    Minimum available agents
                  </label>
                  <input
                    id="target-minimum"
                    type="number"
                    min={0}
                    value={minimumHeadcount}
                    onChange={(event) => setMinimumHeadcount(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                  />
                </div>

                <div>
                  <label htmlFor="target-effective-start" className="mb-1 block text-caption font-bold text-ink-muted">
                    Effective start
                  </label>
                  <input
                    id="target-effective-start"
                    type="date"
                    value={effectiveStart}
                    onChange={(event) => setEffectiveStart(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                  />
                </div>

                <div>
                  <label htmlFor="target-effective-end" className="mb-1 block text-caption font-bold text-ink-muted">
                    Effective end (optional)
                  </label>
                  <input
                    id="target-effective-end"
                    type="date"
                    min={effectiveStart || undefined}
                    value={effectiveEnd}
                    onChange={(event) => setEffectiveEnd(event.target.value)}
                    className="min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveTarget}
                  disabled={!canSave}
                  className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
                >
                  Save target
                </button>
                <button
                  type="button"
                  onClick={() => {
                    resetForm()
                    setFormOpen(false)
                  }}
                  className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {teamTargets.length === 0 ? (
            <EmptyState>
              No staffing targets configured yet. The daily assignment board's coverage strip falls back to its own
              default required headcount until one is.
            </EmptyState>
          ) : (
            <div className="overflow-x-auto rounded-surface border border-line">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    {['Applies to', 'Assignment', 'Qualification', 'Minimum', 'Effective', ''].map((heading) => (
                      <th
                        key={heading}
                        scope="col"
                        className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide text-ink-subtle uppercase"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamTargets.map((target) => (
                    <tr key={target.id}>
                      <td className="border-b border-line px-3 py-2 text-small font-bold">
                        {targetScopeLabel(target)}
                      </td>
                      <td className="border-b border-line px-3 py-2 text-small">{target.assignment ?? 'Any'}</td>
                      <td className="border-b border-line px-3 py-2 text-small">{target.qualification ?? 'None'}</td>
                      <td className="border-b border-line px-3 py-2 text-small [font-variant-numeric:tabular-nums]">
                        {target.minimumHeadcount}
                      </td>
                      <td className="border-b border-line px-3 py-2 text-small">
                        {target.effectiveStart}
                        {target.effectiveEnd ? `–${target.effectiveEnd}` : ' onward'}
                      </td>
                      <td className="border-b border-line px-3 py-2 text-small">
                        <button
                          type="button"
                          onClick={() => removeTarget(target.id)}
                          className="flex min-h-11 items-center justify-center rounded-control px-3 text-caption font-bold text-error hover:bg-error/10"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
